import { useEffect, useState, useMemo, useCallback } from 'react';
import { apiUtils } from '../sdk/api';

// GPT 문서에 따른 통일된 키 사용
const ATK = 'vatton-jwt-token';
const RTK = 'vatton-refresh-token';
const WADDR = 'vatton-wallet-address';
const LTIME = 'vatton-last-login-time';

function computeDefaultApiBase() {
  if (typeof window === 'undefined') return 'https://wallet.vatton.app';
  try {
    const url = new URL(window.location.origin);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      url.hostname = 'localhost';
      url.port = '3600';
      url.protocol = 'http:';
      return url.origin;
    }
    return url.origin;
  } catch (error) {
    return 'https://wallet.vatton.app';
  }
}

export interface User {
  id?: number;
  nickname: string;
  walletAddress: string;
  referralCode?: string;
  referredBy?: string;
  balance?: number;
  tier?: string;
  createdAt?: string;
}

export function useSession() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rawApiBase = (import.meta as any)?.env?.VITE_API_URL ?? '';
  const resolvedApiBase = useMemo(() => {
    const candidate = rawApiBase?.trim();
    const sanitized = candidate ? (candidate.endsWith('/') ? candidate.slice(0, -1) : candidate) : '';
    if (sanitized) return sanitized;
    const fallback = computeDefaultApiBase();
    return fallback.endsWith('/') ? fallback.slice(0, -1) : fallback;
  }, [rawApiBase]);

  const buildUrl = useCallback(
    (path: string) => (resolvedApiBase ? `${resolvedApiBase}${path}` : path),
    [resolvedApiBase],
  );

  const fetchBalance = useCallback(
    async (walletAddress?: string) => {
      if (!walletAddress) return undefined;
      try {
        const balanceRes = await fetch(buildUrl(`/api/blockchain/vttn/balance/${walletAddress}`));
        if (balanceRes.ok) {
          const balanceData = await balanceRes.json();
          return parseFloat(balanceData.balance);
        }
      } catch (balErr) {
        console.warn('VTTN 잔액 조회 실패, DB 값 사용:', balErr);
      }
      return undefined;
    },
    [buildUrl],
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await apiUtils.getMe();
        if (r.ok) {
          const userData = await r.json();
          const resolvedBalance = await fetchBalance(userData.walletAddress);
          if (typeof resolvedBalance === 'number') userData.balance = resolvedBalance;
          if (userData.walletAddress) localStorage.setItem(WADDR, userData.walletAddress);
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Session restore failed:', err);
        setError('세션 복구 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchBalance]);

  const login = async (nickname: string, pin: string) => {
    try {
      setError(null);
      setLoading(true);
      const r = await apiUtils.login(nickname, pin);
      if (!r.ok) {
        const errorData = await r.json();
        throw new Error(errorData.error || '로그인 실패');
      }
      const data = await r.json();
      const resolvedBalance = await fetchBalance(data.user.walletAddress);
      if (typeof resolvedBalance === 'number') data.user.balance = resolvedBalance;
      localStorage.setItem(ATK, data.accessToken);
      localStorage.setItem(RTK, data.refreshToken);
      localStorage.setItem(WADDR, data.user.walletAddress);
      localStorage.setItem(LTIME, String(Date.now()));
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err: any) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (nickname: string, pin: string, phoneNumber?: string) => {
    try {
      setError(null);
      setLoading(true);
      const response = await fetch(buildUrl('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname, pin, phoneNumber }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '회원가입 실패');
      }
      const data = await response.json();
      const resolvedBalance = await fetchBalance(data.user?.walletAddress);
      if (typeof resolvedBalance === 'number') data.user.balance = resolvedBalance;
      if (data.token) {
        localStorage.setItem(ATK, data.token);
        localStorage.setItem(RTK, data.refreshToken);
        localStorage.setItem(WADDR, data.user.walletAddress);
        localStorage.setItem(LTIME, String(Date.now()));
        setUser(data.user);
      }
      return { success: true, user: data.user || data };
    } catch (err: any) {
      console.error('회원가입 오류:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const validateReferralCode = async (code: string) => {
    try {
      const response = await fetch(buildUrl(`/api/referrals/validate`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('vatton-jwt-token')}`,
        },
        body: JSON.stringify({ referralCode: code }),
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, ...data };
      }
      const errorData = await response.json();
      return { success: false, error: errorData.error || '추천코드 검증에 실패했습니다.' };
    } catch (error) {
      console.error('추천코드 검증 실패:', error);
      return { success: false, error: '추천코드 검증 중 오류가 발생했습니다.' };
    }
  };

  const logout = async () => {
    try {
      const r = await fetch(buildUrl('/api/auth/logout'), { method: 'POST' });
      if (!r.ok) {
        const errorData = await r.json();
        console.warn('Logout warning:', errorData);
      }
    } catch (err) {
      console.error('Logout API failed:', err);
    } finally {
      for (const k of [ATK, RTK, WADDR, LTIME]) localStorage.removeItem(k);
      setUser(null);
      setError(null);
    }
  };

  const clearError = () => setError(null);

  const claimWelcomeBonus = async () => {
    try {
      setError(null);
      setLoading(true);
      const r = await apiUtils.claimWelcomeBonus();
      if (!r.ok) {
        const errorData = await r.json();
        throw new Error(errorData.error || '웰컴 보너스 클레임에 실패했습니다.');
      }
      const data = await r.json();
      return { success: true, data };
    } catch (err: any) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    clearError,
    validateReferralCode,
    claimWelcomeBonus,
    isAuthenticated: !!user,
    isLoading: loading,
    accessToken: localStorage.getItem(ATK),
  };
}